import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, Drawer, List,
  Divider, IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Dashboard, CalendarMonth, Report, Storefront, People,
  AccountBalance, Block, PlayArrow, Menu as MenuIcon, LocalCafe, Chat,
  TrendingUp, Receipt, Campaign, CurrencyExchange, ListAlt, DateRange,
  History, Settings, VerifiedUser,
} from '@mui/icons-material';
import { useState } from 'react';
import { colors, designTokens } from '../../theme/theme';
import NavListItem from '../shared/NavListItem';
import NotificationsBell from '../notifications/NotificationsBell';

const DRAWER_WIDTH = 260;

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', icon: <Dashboard />, path: '/admin' },
      { label: 'Analytics', icon: <TrendingUp />, path: '/admin/analytics' },
    ],
  },
  {
    label: 'People',
    items: [
      { label: 'Users', icon: <People />, path: '/admin/users' },
      { label: 'Partners', icon: <Storefront />, path: '/admin/partners' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Events', icon: <CalendarMonth />, path: '/admin/events' },
      { label: 'Calendar', icon: <DateRange />, path: '/admin/calendar' },
      { label: 'Venues', icon: <LocalCafe />, path: '/admin/venues' },
    ],
  },
  {
    label: 'Support',
    items: [
      { label: 'Chat', icon: <Chat />, path: '/admin/support' },
      { label: 'Reports', icon: <Report />, path: '/admin/reports' },
      { label: 'Moderation', icon: <VerifiedUser />, path: '/admin/moderation' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { label: 'Revenue', icon: <AccountBalance />, path: '/admin/finance' },
      { label: 'Ledger', icon: <ListAlt />, path: '/admin/ledger' },
      { label: 'Settlements', icon: <CurrencyExchange />, path: '/admin/settlements' },
      { label: 'Refunds', icon: <Receipt />, path: '/admin/refunds' },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Jobs', icon: <PlayArrow />, path: '/admin/jobs' },
      { label: 'Blacklist', icon: <Block />, path: '/admin/blacklist' },
      { label: 'Broadcast', icon: <Campaign />, path: '/admin/broadcast' },
      { label: 'Audit Log', icon: <History />, path: '/admin/audit' },
      { label: 'Settings', icon: <Settings />, path: '/admin/settings' },
    ],
  },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const drawer = (
    <Box sx={{ pt: 2 }}>
      <Typography variant="h6" sx={{ px: 2, pb: 2, fontWeight: 700, color: colors.admin.primary }}>
        Admin
      </Typography>
      <Divider />
      <Box sx={{ pt: 1 }}>
        {menuSections.map((section, sIdx) => (
          <Box key={section.label}>
            {sIdx > 0 && <Divider sx={{ mx: 2, my: 0.5 }} />}
            <Typography
              variant="overline"
              sx={{
                px: 2, pt: 1.5, pb: 0.5,
                display: 'block',
                color: colors.neutral[400],
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
              }}
            >
              {section.label}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => (
                <NavListItem
                  key={item.path}
                  to={item.path}
                  label={item.label}
                  icon={item.icon}
                  role="admin"
                  density="compact"
                  active={location.pathname === item.path}
                  onNavigate={() => { navigate(item.path); setMobileOpen(false); }}
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
          {drawer}
        </Drawer>
      ) : (
        <Drawer variant="permanent" sx={{ width: DRAWER_WIDTH, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, borderRight: `1px solid ${colors.neutral[200]}` } }}>
          {drawer}
        </Drawer>
      )}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {isMobile && (
          <AppBar position="sticky" elevation={0} sx={{ bgcolor: colors.background.paper }}>
            <Toolbar>
              <IconButton onClick={() => setMobileOpen(true)} edge="start"><MenuIcon /></IconButton>
              <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: colors.admin.primary }}>Admin</Typography>
              <NotificationsBell />
            </Toolbar>
          </AppBar>
        )}
        {/* Padding follows designTokens.spacing.page (xs:2/sm:3/md:4);
            desktop bell shares the same rhythm. */}
        <Box
          sx={{
            flex: 1,
            px: designTokens.spacing.page,
            py: designTokens.spacing.page,
            bgcolor: colors.background.default,
            position: 'relative',
          }}
        >
          {/* Bell pinned top-right on desktop (no app bar there). */}
          {!isMobile && (
            <Box
              sx={{
                position: 'absolute',
                top: designTokens.spacing.page,
                right: designTokens.spacing.page,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <NotificationsBell />
            </Box>
          )}
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
